import { useState, useCallback, useEffect, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DemoModeContext, DEMO_MODE_KEY } from "@/hooks/useDemoMode";

export function DemoModeProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize from sessionStorage to persist across navigation
  const [isDemoMode, setIsDemoMode] = useState(() => {
    try {
      return sessionStorage.getItem(DEMO_MODE_KEY) === "true";
    } catch {
      return false;
    }
  });

  // Sync to sessionStorage
  useEffect(() => {
    try {
      if (isDemoMode) {
        sessionStorage.setItem(DEMO_MODE_KEY, "true");
      } else {
        sessionStorage.removeItem(DEMO_MODE_KEY);
      }
    } catch {
      // Ignore storage errors
    }
  }, [isDemoMode]);

  const enterDemoMode = useCallback(() => {
    setIsDemoMode(true);
    // Navigate to overview if not already on a coach page
    if (!location.pathname.startsWith("/coach")) {
      navigate("/coach/overview");
    }
  }, [navigate, location.pathname]);

  const exitDemoMode = useCallback(() => {
    setIsDemoMode(false);
    // Stay on the same page, data will switch to real data
    navigate("/coach/overview");
  }, [navigate]);

  return (
    <DemoModeContext.Provider value={{ isDemoMode, enterDemoMode, exitDemoMode }}>
      {children}
    </DemoModeContext.Provider>
  );
}
